import type {CreateExtArgs} from '@wix/document-manager-core'
import type {CompRef, ResponsiveBreakpointsDataOptions, SingleLayoutData} from '@wix/document-services-types'
import {DATA_TYPES} from '../../constants/constants'
import type {VariantsExtensionAPI} from '../variants/variants'
import type {StructureExtensionAPI} from '../structure'

const DEFAULT_MIN_CHILDREN_COUNT = 3

type FilterFunction<T> = (args: T, options?: ResponsiveBreakpointsDataOptions) => boolean

const disableableFilter =
    <T>(fn: FilterFunction<T>): FilterFunction<T> =>
    (args, options) => {
        if (options?.disableFilters) return false
        return fn(args, options)
    }

export const createFilters = ({extensionAPI}: CreateExtArgs) => {
    const variantsApi = () => (extensionAPI as VariantsExtensionAPI).variants

    const isHiddenComponent: FilterFunction<{compPointer: CompRef}> = ({compPointer}) => {
        const layoutData = variantsApi().getComponentItemConsideringVariants(compPointer, DATA_TYPES.layout)
        return layoutData?.componentLayout?.hidden === true
    }

    const shouldExcludeComponent: FilterFunction<{compPointer: CompRef}> = ({compPointer}) =>
        isHiddenComponent({compPointer})

    const filterVisibleChildren = (
        {children}: {children: CompRef[]},
        options?: ResponsiveBreakpointsDataOptions
    ): CompRef[] => children.filter(compPointer => !shouldExcludeComponent({compPointer}, options))

    const shouldExcludeSection = disableableFilter<{sectionPointer: CompRef}>(({sectionPointer}, options) => {
        const minChildrenCount = options?.minChildrenCount ?? DEFAULT_MIN_CHILDREN_COUNT
        const {components} = extensionAPI as StructureExtensionAPI
        return components.getChildren(sectionPointer, true).length < minChildrenCount
    })

    const isLayoutRefWidthOutOfBounds = disableableFilter<{
        layout: SingleLayoutData | undefined
        min?: number
        max?: number
    }>(({layout, min, max}) => {
        const refWidth = layout?.spx?.refWidth
        if (!refWidth) return false
        if (min !== undefined && refWidth < min) return true
        if (max !== undefined && refWidth > max) return true
        return false
    })

    return {
        isLayoutRefWidthOutOfBounds,
        shouldExcludeComponent,
        shouldExcludeSection,
        filterVisibleChildren
    }
}

export type FiltersAPI = ReturnType<typeof createFilters>
